import React from 'react';
import { PageHeader, ScrollReveal, StatsBar, TrustBadges, CTASection } from '../components/UI';
import IMAGES from '../images';

const About = () => {
  const values = [
    { icon: '🎯', title: 'Results-Obsessed', desc: 'We measure success by your growth, not vanity metrics. Every strategy is designed to drive real, measurable business outcomes.' },
    { icon: '🔍', title: 'Radical Transparency', desc: 'No black boxes. No jargon. You\'ll always know exactly what we\'re doing, why we\'re doing it, and what results it\'s generating.' },
    { icon: '🤝', title: 'Partnership Mindset', desc: 'We\'re not just vendors—we\'re partners in your success. Your wins are our wins, and we\'re invested in your long-term growth.' },
    { icon: '📚', title: 'Continuous Learning', desc: 'Digital marketing evolves constantly. We stay ahead of algorithm updates, new features, and industry trends so you don\'t have to.' },
    { icon: '💪', title: 'No Excuses', desc: 'We take ownership of outcomes. If something isn\'t working, we fix it. No finger-pointing, no excuses—just solutions.' },
    { icon: '🏆', title: 'Excellence in Everything', desc: 'From campaign setup to client communication, we hold ourselves to the highest standards. Good enough isn\'t good enough.' },
  ];

  const timeline = [
    { year: '2017', title: 'The Beginning', desc: 'Started as a freelance digital marketer, frustrated with agencies that overpromise and underdeliver.' },
    { year: '2019', title: 'First Major Win', desc: 'Helped a struggling roofing company increase leads by 400%. Word started spreading.' },
    { year: '2021', title: 'Crescit Founded', desc: 'Officially launched Crescit Marketing with a mission to help local businesses compete with the big guys.' },
    { year: '2023', title: '50+ Clients', desc: 'Reached milestone of 50 active clients and $500K+ in managed ad spend.' },
    { year: '2024', title: 'Top Rated Plus', desc: 'Achieved Upwork\'s Top Rated Plus status with 4.9/5 client rating.' },
  ];

  return (
    <div>
      <PageHeader 
        title="About Crescit Marketing"
        subtitle="We're not your typical marketing agency. We're a team of results-obsessed specialists dedicated to helping local businesses dominate their markets."
        breadcrumb="About Us"
      />

      <StatsBar />

      {/* Our Story */}
      <section style={{ padding: '80px 24px', background: 'white' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, alignItems: 'center' }}>
            <ScrollReveal>
              <div>
                <span style={{ color: '#ff6b35', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: 2 }}>Our Story</span>
                <h2 style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 800, color: '#1a1a2e', marginTop: 10, marginBottom: 20 }}>
                  Built From Frustration, Driven By Results
                </h2>
                <p style={{ color: '#555', fontSize: 16, lineHeight: 1.8, marginBottom: 16 }}>
                  We started Crescit Marketing because we were tired of seeing local businesses get burned by marketing agencies. You know the story: big promises, fancy reports full of meaningless metrics, 12-month contracts, and zero actual results.
                </p>
                <p style={{ color: '#555', fontSize: 16, lineHeight: 1.8, marginBottom: 16 }}>
                  We believed there had to be a better way. A way to deliver enterprise-level marketing expertise to local businesses without the BS, the jargon, or the excuses.
                </p>
                <p style={{ color: '#555', fontSize: 16, lineHeight: 1.8, marginBottom: 24 }}>
                  Today, we've helped over 50 businesses transform their online presence. Our clients see real results: more traffic, more leads, more customers. And we do it all with complete transparency—no smoke and mirrors.
                </p>
                <TrustBadges />
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <div style={{ position: 'relative' }}>
                <div style={{ width: '100%', paddingTop: '100%', borderRadius: 20, overflow: 'hidden', border: '4px solid #f8f9fa' }}>
                  <img src={IMAGES.teamCoo} alt="Our Team" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
                <div style={{ position: 'absolute', bottom: -20, right: -20, background: 'white', borderRadius: 16, padding: 24, boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
                  <div style={{ fontSize: 36, fontWeight: 800, color: '#ff6b35' }}>7+</div>
                  <div style={{ color: '#666', fontSize: 14 }}>Years Experience</div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section style={{ padding: '80px 24px', background: '#f8f9fa' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <ScrollReveal>
            <div style={{ textAlign: 'center', marginBottom: 50 }}>
              <span style={{ color: '#ff6b35', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: 2 }}>Our Journey</span>
              <h2 style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 800, color: '#1a1a2e', marginTop: 10 }}>The Crescit Story</h2>
            </div>
          </ScrollReveal>
          <div style={{ position: 'relative' }}>
            <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 2, background: '#ddd', transform: 'translateX(-50%)' }} />
            {timeline.map((item, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 40, flexDirection: i % 2 === 0 ? 'row' : 'row-reverse' }}>
                  <div style={{ flex: 1, padding: '0 40px', textAlign: i % 2 === 0 ? 'right' : 'left' }}>
                    <div style={{ background: 'white', borderRadius: 16, padding: 24, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                      <div style={{ color: '#ff6b35', fontWeight: 800, fontSize: 18, marginBottom: 8 }}>{item.year}</div>
                      <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e', marginBottom: 8 }}>{item.title}</h3>
                      <p style={{ color: '#666', fontSize: 14, lineHeight: 1.6, margin: 0 }}>{item.desc}</p>
                    </div>
                  </div>
                  <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'linear-gradient(135deg, #ff6b35, #f54e00)', border: '4px solid white', boxShadow: '0 0 0 4px #f8f9fa', zIndex: 1 }} />
                  <div style={{ flex: 1 }} />
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section style={{ padding: '80px 24px', background: 'white' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <ScrollReveal>
            <div style={{ textAlign: 'center', marginBottom: 50 }}>
              <span style={{ color: '#ff6b35', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: 2 }}>Our Values</span>
              <h2 style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 800, color: '#1a1a2e', marginTop: 10, marginBottom: 16 }}>What We Stand For</h2>
              <p style={{ color: '#666', maxWidth: 600, margin: '0 auto', fontSize: 17, lineHeight: 1.7 }}>
                These aren't just words on a wall. These are the principles that guide every decision we make.
              </p>
            </div>
          </ScrollReveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 24 }}>
            {values.map((value, i) => (
              <ScrollReveal key={i} delay={i * 0.08}>
                <div style={{ display: 'flex', gap: 16, padding: 28, background: '#f8f9fa', borderRadius: 16 }}>
                  <div style={{ fontSize: 36 }}>{value.icon}</div>
                  <div>
                    <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e', marginBottom: 8 }}>{value.title}</h3>
                    <p style={{ color: '#666', fontSize: 15, lineHeight: 1.7, margin: 0 }}>{value.desc}</p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section style={{ padding: '80px 24px', background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto', textAlign: 'center' }}>
          <ScrollReveal>
            <span style={{ color: '#ff6b35', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: 2 }}>Why Crescit</span>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 800, color: 'white', marginTop: 10, marginBottom: 40 }}>
              What Makes Us Different
            </h2>
          </ScrollReveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24, textAlign: 'left' }}>
            {[
              { title: 'Specialized Focus', desc: 'We don\'t try to do everything. We specialize in Google Ads and SEO for local businesses—and we\'re really good at it.' },
              { title: 'No Long-Term Contracts', desc: 'We earn your business every month. No 12-month lock-ins. Our clients stay because they see results.' },
              { title: 'Transparent Reporting', desc: 'Real-time dashboards, detailed monthly reports, and regular strategy calls. You always know what\'s happening.' },
              { title: 'Dedicated Support', desc: 'Your account manager is always a call or email away. We respond in hours, not days.' },
            ].map((item, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 16, padding: 28, border: '1px solid rgba(255,255,255,0.1)' }}>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: 'white', marginBottom: 10 }}>{item.title}</h3>
                  <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 15, lineHeight: 1.7, margin: 0 }}>{item.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
};

export default About;
